import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Slide,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Award,
  CheckCircle,
  Circle,
  CloudDownload,
  Download,
  Eye,
  X,
  ZoomIn
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

import GetAppIcon from "@mui/icons-material/GetApp";
import { TransitionProps } from "@mui/material/transitions";

import { useForm } from "react-hook-form";
import useFileUploadHandler from "../../../useFilesHandler";
import {
  base64ToString,
  decryptAES256CBC2,
  downloadBase64PDF,
  encryptWithFixedKey,
  generateRandomBytes
} from "../../../utils";
import CustomAccordion from "../../common/CustomAccordion";
import useScrutinyServices from "../../../context/useScrutinyServices";
import { FaQuestionCircle } from "react-icons/fa";
import {
  ObjectionHandlerTypes,
  ObjectionHandlerTypesWrapper
} from "../../../types/onlineExam.interface";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function ZoomModal({ isOpen, onClose, content }: any) {
  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      className="p-0"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <Eye className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {content.type === "question" ? "Question Details" : `Option`}
              </h3>
              <p className="text-sm text-gray-500">Detailed view</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {content.image && (
            <div className="mb-6">
              <img
                src={content.image}
                alt={content.type === "question" ? "Question" : "Option"}
                className="w-full !min-h-[300px] object-contain rounded-xl border border-gray-200 shadow-sm"
              />
            </div>
          )}

          <div className="prose prose-gray max-w-none">
            <p className="text-base leading-relaxed text-gray-800 whitespace-pre-wrap">
              {content.text}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

type ScrutinyDetailsProps = {
  selectedScrutiny: any;
  handleClose: () => void;

  refreshList?: () => void;
};
const ScrutinyDetails: FC<ScrutinyDetailsProps> = ({
  selectedScrutiny,
  refreshList,
  handleClose
}) => {
  const [questionPaperInfo, setQuestionPaperInfo] = useState<boolean>(true);
  const [selectedScrutinyDetails, setSelectedScrutinyDetails] = useState<
    any | null
  >(null);
  const scrutinyContext = useScrutinyServices();
  const [approvalComment, setApprovalComment] = useState<string>("");
  const [candidateRemarks, setCandidateRemarks] = useState<boolean>(true);
  // const  scrutinyContext = useScrutinyContext()
  const [base64Docs, setBase64Docs] = useState<any>();
  const [uploadFile, setUploadFile] = useState<any>();
  const [answerPaperInfo, setAnswerPaperInfo] = useState<boolean>(true);
  const FormHandler = useForm();
  const [zoomModal, setZoomModal] = useState<{
    isOpen: boolean;
    content: {
      type: "question" | "option";
      text: string;
      image?: string;
      optionKey?: string;
    };
  }>({
    isOpen: false,
    content: { type: "question", text: "" },
  });
  const openZoom = (
    type: "question" | "option",
    text: string,
    image?: string,
    optionKey?: string
  ) => {
    setZoomModal({
      isOpen: true,
      content: { type, text, image, optionKey },
    });
  };

  const closeZoom = () => {
    setZoomModal({ isOpen: false, content: { type: "question", text: "" } });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileUploadHandler = useFileUploadHandler({
    fileuploadFinallyCallback: () => {
      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
    },
  });

  const getSelectedOption = (item: any) => {
    // const isCorrect =
    //   item[optionKey] === item["option_actual_ancer_id"] &&
    //   examStatus &&
    //   examStatus?.toLowerCase() === "published";

    if (item?.option_a_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_a,
        selected_option_image: item?.option_a_img,
      };
    }

    if (item?.option_b_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_b,
        selected_option_image: item?.option_b_img,
      };
    }
    if (item?.option_c_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_c,
        selected_option_image: item?.option_c_img,
      };
    }
    if (item?.option_d_id === item?.selected_option_id) {
      return {
        selected_option_text: item?.option_d,
        selected_option_image: item?.option_d_img,
      };
    }
  };

  useEffect(() => {
    (async () => {
      if (selectedScrutiny) {
        const encryption_key = generateRandomBytes();

        let rawPayload = {
          id: selectedScrutiny.id,
          encryption_key,
        };
        const payload = encryptWithFixedKey(rawPayload);
        const response = await scrutinyContext.getScrutinyListingHandler({
          payload,
        });
        if (response && response?.response) {
          const rawResponse = await decryptAES256CBC2(
            response?.response,
            btoa(encryption_key)
          );
          const responsedData = JSON.parse(rawResponse);
          console.log("responsedData >>>> ", responsedData);
          if (
            responsedData &&
            responsedData?.data &&
            Array.isArray(responsedData?.data) &&
            responsedData?.data?.length
          )
            setSelectedScrutinyDetails(responsedData?.data?.[0]);
        }
        // if (response && Array.isArray(response) && response?.length) {
        //   setSelectedScrutinyDetails(response?.[0]);
        // } else if (response) {
        //   setSelectedScrutinyDetails(response?.[0]);
        // }
      }
    })();
  }, [selectedScrutiny]);

  const getCorrectOption = (item: any) => {
    // const isCorrect =
    //   item[optionKey] === item["option_actual_ancer_id"] &&
    //   examStatus &&
    //   examStatus?.toLowerCase() === "published";

    if (item?.option_a_id === item?.option_actual_answer_id) {
      return {
        selected_option_text: item?.option_a,
        selected_option_image: item?.option_a_img,
      };
    }

    if (item?.option_b_id === item?.option_actual_answer_id) {
      return {
        selected_option_text: item?.option_b,
        selected_option_image: item?.option_b_img,
      };
    }
    if (item?.option_c_id === item?.option_actual_answer_id) {
      return {
        selected_option_text: item?.option_c,
        selected_option_image: item?.option_c_img,
      };
    }
    if (item?.option_d_id === item?.option_actual_answer_id) {
      return {
        selected_option_text: item?.option_d,
        selected_option_image: item?.option_d_img,
      };
    }
  };

  const submitScrtinyHandler = () => {
    if (approvalComment?.trim()?.length === 0)
      return toast.error("Please write supporting comment");
    // //     examsContext.ApplyScrutinyHandler({
    // // payload:{
    // //     exam_group_id:examsDetailsTypes?.exam_group??0,
    // //     option_id:selectedAnswer?.id??0,
    // //     regenerate_result:false,
    // //     remarks:approvalComment.trim(),
    // //     supporting_docs:fileUploadHandler?.images?.length && fileUploadHandler?.images[0]?.fileimage ? fileUploadHandler?.images[0]?.fileimage?.split(",")?.[1] :""
    // // },
    // successCallback:()=>{
    //     handleClose();
    //     fileUploadHandler.clearAllFiles();
    //     setApprovalComment("");
    //     handleCloseParent?.();
    //     setSelectedAnswer(null);
    //     refreshList?.();
    // }

    // })
  };

  const getOptionClasses = (item: any, optionKey: string) => {
    const isSelected = item[optionKey] === item["selected_option_id"];
    const isCorrect = item[optionKey] === item["option_actual_answer_id"];

    if (isSelected && isCorrect) {
      return "bg-gradient-to-r from-green-100 to-emerald-100 border-green-300 text-green-900 shadow-sm";
    } else if (isSelected && !isCorrect) {
      return "bg-gradient-to-r from-blue-100 to-indigo-100 border-blue-300 text-blue-900 shadow-sm";
    } else if (!isSelected && isCorrect) {
      return "bg-gradient-to-r from-green-100 to-green-100 border-green-200 text-green-800";
    }
    return "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200 text-gray-700 hover:border-gray-300";
  };

  useEffect(() => {
    if (!selectedScrutiny) {
      fileUploadHandler.clearAllFiles();
      setApprovalComment("");
      setSelectedScrutinyDetails(null);
    }
  }, [selectedScrutiny]);

  return (
    <Dialog
      fullWidth
      fullScreen
      open={!!selectedScrutiny}
      onClose={handleClose}
      TransitionComponent={Transition}
      className="p-0"
    >
      {selectedScrutinyDetails ? (
        <>
          <div className="py-2 w-full bg-blue-500 flex items-center justify-between  md:px-8 ">
            <p className="text-white text-2xl font-semibold ">
              Candidate Raise Objection
            </p>
            <IconButton
              onClick={handleClose}
              className="text-white"
              aria-label="delete"
            >
              <CloseIcon className="text-white" />
            </IconButton>
          </div>
          <DialogContent>
            <div className="mx-4">
              {!!selectedScrutinyDetails ? (
                <CustomAccordion
                  title={"Scrutiny Application"}
                  isExpanded={answerPaperInfo}
                  setExpanded={setAnswerPaperInfo}
                  secondary
                  // highlightColor={"bg-[#c026d3]"}
                >
                  <div className="col-span-12  mb-2 ">
                    <div
                      className={`font-semibold w-full py-1 2xl:px-4 flex items-center justify-between  ${
                        selectedScrutinyDetails?.accept_flag === null
                          ? `bg-yellow-50 text-yellow-500`
                          : selectedScrutinyDetails?.accept_flag
                          ? `bg-emerald-50 text-emerald-500`
                          : `bg-red-50 text-red-500`
                      } `}
                    >
                      <p>Application Status</p>
                      <p>
                        {selectedScrutinyDetails?.accept_flag === null
                          ? `Pending`
                          : selectedScrutinyDetails?.accept_flag
                          ? `Accepted`
                          : `Rejected`}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 border-b border-gray-100/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {/* <div
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                item.correct_flag
                  ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200"
                  : "bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200"
              }`}
            >
              {item.correct_flag ? "✓ Correct" : "✗ Incorrect"}

            </div> */}

                        <div className="flex mt-2 mb-1  items-center gap-x-4">
                          <div
                            className={`flex mt-2  text-xs px-2 py-1 font-semibold text-white rounded-sm ${
                              selectedScrutinyDetails.obtain_marks?.startsWith(
                                "-"
                              )
                                ? `bg-red-600`
                                : `bg-green-500`
                            }`}
                          >
                            Obtained Marks :{" "}
                            {selectedScrutinyDetails.obtain_marks}
                          </div>

                          <div
                            className={`flex mt-2  text-xs px-2 py-1 font-semibold text-white bg-blue-700 rounded-sm`}
                          >
                            Exam Paper : {selectedScrutiny?.exam_paper}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 text-xs">
                        {selectedScrutinyDetails.marks ? (
                          <div className="flex items-center space-x-2 bg-amber-600 px-3  rounded-sm py-1 ">
                            <Award className="w-4 h-4 text-white" />
                            <p className="text-xs text-white">
                              Allotted Marks :{" "}
                            </p>
                            <span className="font-medium text-white">
                              {selectedScrutinyDetails.marks}
                            </span>
                          </div>
                        ) : (
                          <></>
                        )}

                        {selectedScrutinyDetails?.negetive_mark ? (
                          <div className="flex items-center space-x-2 bg-red-500 px-3 rounded-sm py-1">
                            <AlertCircle className="w-4 h-4 text-white" />
                            <p className="text-xs text-white">
                              Negative Marks :{" "}
                            </p>
                            <span className="font-medium text-white">
                              {selectedScrutinyDetails.negetive_mark}
                            </span>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="p-4">
                    {/* Question with Zoom */}
                    <div className="mb-4">
                      <div className="flex flex-col gap-y-4">
                        {selectedScrutinyDetails?.question?.trim()?.length ? (
                          <div className="flex-1">
                            <div className="flex items-start space-x-3 px-2  ">
                              <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center !text-white text-sm font-bold">
                                <FaQuestionCircle
                                  className="text-white"
                                  size={15}
                                />
                              </div>
                              <p className="text-sm font-medium text-gray-900 leading-relaxed ">
                                {selectedScrutinyDetails.question}
                              </p>

                              <button
                                onClick={() =>
                                  openZoom(
                                    "question",
                                    selectedScrutinyDetails.question,
                                    selectedScrutinyDetails?.question_img
                                  )
                                }
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-lg"
                                title="View full question"
                              >
                                <ZoomIn className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <></>
                        )}

                        {selectedScrutinyDetails.question_img && (
                          <div className="w-full flex items-center gap-x-4">
                            <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center !text-white text-sm font-bold">
                              <FaQuestionCircle
                                className="text-white"
                                size={15}
                              />
                            </div>
                            <img
                              // src={item.question_image_url}
                              src={base64ToString(
                                `${selectedScrutinyDetails.question_img}`
                              )}
                              alt="Question"
                              className="!w-full !h-[100px] object-contain rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() =>
                                openZoom(
                                  "question",
                                  selectedScrutinyDetails.question_text,
                                  base64ToString(
                                    `${selectedScrutinyDetails.question_img}`
                                  )
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid  grid-cols-1 md:grid-cols-2  gap-x-4 gap-y-4 mb-3">
                      {selectedScrutinyDetails?.option_a ||
                      selectedScrutinyDetails?.option_a_img ? (
                        <div
                          className={`p-3 rounded-xl border transition-all duration-200 group    ${getOptionClasses(
                            selectedScrutinyDetails,
                            "option_a_id"
                          )}`}
                        >
                          <div className="flex items-center gap-x-4">
                            {selectedScrutinyDetails?.option_a?.trim()
                              ?.length ? (
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className="text-sm text-wrap !text-black">
                                  {selectedScrutinyDetails?.option_a}
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}

                            <div className="flex items-center space-x-2  cursor-pointer ">
                              {selectedScrutinyDetails.option_a_img && (
                                <>
                                  <img
                                    src={`${base64ToString(
                                      `${selectedScrutinyDetails.option_a_img}`
                                    )}`}
                                    alt={`Option image`}
                                    className="!w-[80%] !h-16 object-contain rounded border"
                                  />
                                  <ZoomIn
                                    className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() =>
                                      openZoom(
                                        "option",
                                        selectedScrutinyDetails?.option_a,
                                        `${base64ToString(
                                          `${selectedScrutinyDetails.option_a_img}`
                                        )}`,
                                        "option_a_img"
                                      )
                                    }
                                  />
                                </>
                              )}

                              {/* {getOptionIcon(item, optionKey)} */}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}

                      {selectedScrutinyDetails?.option_b ||
                      selectedScrutinyDetails?.option_b_img ? (
                        <div
                          className={`p-3 rounded-xl border transition-all duration-200 group   ${getOptionClasses(
                            selectedScrutinyDetails,
                            "option_b_id"
                          )}`}
                          // onClick={() =>
                          //   openZoom(
                          //     "option",
                          //     optionData.text,
                          //     optionData.image_url,
                          //     optionKey
                          //   )
                          // }
                        >
                          <div className="flex items-center gap-x-4">
                            {selectedScrutinyDetails?.option_b?.trim()
                              ?.length ? (
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className="text-sm text-wrap">
                                  {selectedScrutinyDetails?.option_b}
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}
                            <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                              {selectedScrutinyDetails.option_b_img ? (
                                <>
                                  <img
                                    src={`${base64ToString(
                                      `${selectedScrutinyDetails.option_b_img}`
                                    )}`}
                                    alt={`Option image`}
                                    className="!w-[80%] !h-16 object-contain rounded border"
                                  />
                                  <ZoomIn
                                    className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() =>
                                      openZoom(
                                        "option",
                                        selectedScrutinyDetails?.option_b,
                                        `${base64ToString(
                                          `${selectedScrutinyDetails.option_b_img}`
                                        )}`,
                                        "option_b_img"
                                      )
                                    }
                                  />
                                </>
                              ) : (
                                <></>
                              )}

                              {/* {getOptionIcon(item, optionKey)} */}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}

                      {selectedScrutinyDetails?.option_c ||
                      selectedScrutinyDetails?.option_c_img ? (
                        <div
                          className={`p-3 rounded-xl border transition-all duration-200 group   ${getOptionClasses(
                            selectedScrutinyDetails,
                            "option_c_id"
                          )}`}
                          // onClick={() =>
                          //   openZoom(
                          //     "option",
                          //     optionData.text,
                          //     optionData.image_url,
                          //     optionKey
                          //   )
                          // }
                        >
                          <div className="flex items-center gap-x-4">
                            {selectedScrutinyDetails?.option_c?.trim()
                              ?.length ? (
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className="text-sm text-wrap">
                                  {selectedScrutinyDetails?.option_c}
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}
                            <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                              {selectedScrutinyDetails.option_c_img ? (
                                <>
                                  <img
                                    src={`${base64ToString(
                                      `${selectedScrutinyDetails.option_c_img}`
                                    )}`}
                                    alt={`Option image`}
                                    className="!w-[80%] !h-16 object-contain rounded border"
                                  />
                                  <ZoomIn
                                    className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() =>
                                      openZoom(
                                        "option",
                                        selectedScrutinyDetails?.option_c,
                                        `${base64ToString(
                                          `${selectedScrutinyDetails.option_c_img}`
                                        )}`,
                                        "option_c_img"
                                      )
                                    }
                                  />
                                </>
                              ) : (
                                <></>
                              )}

                              {/* {getOptionIcon(item, optionKey)} */}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}

                      {selectedScrutinyDetails?.option_d ||
                      selectedScrutinyDetails?.option_d_img ? (
                        <div
                          className={`p-3 rounded-xl border transition-all duration-200 group    ${getOptionClasses(
                            selectedScrutinyDetails,
                            "option_d_id"
                          )}`}
                        >
                          <div className="flex items-center gap-x-4">
                            {selectedScrutinyDetails?.option_d?.trim()
                              ?.length ? (
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className="text-sm text-wrap">
                                  {selectedScrutinyDetails?.option_d}
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}

                            <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                              {selectedScrutinyDetails.option_d_img ? (
                                <>
                                  <img
                                    src={`${base64ToString(
                                      `${selectedScrutinyDetails.option_d_img}`
                                    )}`}
                                    alt={`Option image`}
                                    className="!w-[80%] !h-16 object-contain rounded border"
                                  />
                                  <ZoomIn
                                    className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() =>
                                      openZoom(
                                        "option",
                                        selectedScrutinyDetails?.option_d,
                                        `${base64ToString(
                                          `${selectedScrutinyDetails.option_d_img}`
                                        )}`,
                                        "option_d_img"
                                      )
                                    }
                                  />
                                </>
                              ) : (
                                <></>
                              )}

                              {/* {getOptionIcon(item, optionKey)} */}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>

                    {!selectedScrutinyDetails.selected_option_id ? (
                      <div className="flex mt-2 mb-1 w-full   ">
                        <div
                          className={`flex mt-2  text-xs px-2 py-1 font-semibold text-white rounded-sm bg-blue-700 `}
                        >
                          Not Attempted
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div className="flex items-center justify-between text-xs bg-gray-50/50 rounded-xl p-3">
                      <div className="flex flex-col gap-y-4">
                        {getSelectedOption(selectedScrutinyDetails) ? (
                          <div className="flex flex-col gap-y-3">
                            <div className="flex items-center gap-x-4">
                              <Circle className="w-3 h-3 text-blue-600 fill-current" />
                              <p className="text-md text-blue-600  font-bold">
                                Selected Answer
                              </p>
                            </div>

                            <div
                              className={`p-3 rounded-xl border transition-all duration-200 group   `}
                            >
                              <div className="flex items-center gap-x-4">
                                {getSelectedOption(
                                  selectedScrutinyDetails
                                )?.selected_option_text?.trim()?.length ? (
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <span className="text-sm text-wrap">
                                      {
                                        getSelectedOption(
                                          selectedScrutinyDetails
                                        )?.selected_option_text
                                      }
                                    </span>
                                  </div>
                                ) : (
                                  <></>
                                )}
                                <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                                  {getSelectedOption(selectedScrutinyDetails)
                                    ?.selected_option_image ? (
                                    <>
                                      <img
                                        src={`${base64ToString(
                                          `${
                                            getSelectedOption(
                                              selectedScrutinyDetails
                                            )?.selected_option_image
                                          }`
                                        )}`}
                                        alt={`Option image`}
                                        className="!w-[80%] !h-16 object-contain rounded border"
                                      />
                                      <ZoomIn
                                        className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() =>
                                          openZoom(
                                            "option",
                                            selectedScrutinyDetails?.option_c,
                                            `${base64ToString(
                                              `${
                                                getSelectedOption(
                                                  selectedScrutinyDetails
                                                )?.selected_option_image
                                              }`
                                            )}`,
                                            "option_c_img"
                                          )
                                        }
                                      />
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <></>
                        )}

                        <div className="flex flex-col gap-y-3">
                          <div className="flex items-center gap-x-4">
                            <CheckCircle className="w-3 h-3 text-green-600 fill-current" />
                            <p className="text-md font-bold text-green-600">
                              Correct Answer
                            </p>
                          </div>
                          {getCorrectOption(selectedScrutinyDetails) ? (
                            <div
                              className={`p-3 rounded-xl border transition-all duration-200 group   `}
                            >
                              <div className="flex items-center gap-x-4">
                                {getCorrectOption(
                                  selectedScrutinyDetails
                                )?.selected_option_text?.trim()?.length ? (
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <span className="text-sm text-wrap">
                                      {
                                        getCorrectOption(
                                          selectedScrutinyDetails
                                        )?.selected_option_text
                                      }
                                    </span>
                                  </div>
                                ) : (
                                  <></>
                                )}
                                <div className="flex items-center space-x-2 flex-shrink-0 cursor-pointer">
                                  {getCorrectOption(selectedScrutinyDetails)
                                    ?.selected_option_image ? (
                                    <>
                                      <img
                                        src={`${base64ToString(
                                          `${
                                            getCorrectOption(
                                              selectedScrutinyDetails
                                            )?.selected_option_image
                                          }`
                                        )}`}
                                        alt={`Option image`}
                                        className="!w-[90%] !h-16 object-contain rounded border"
                                      />
                                      <ZoomIn
                                        className="w-6 h-6 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() =>
                                          openZoom(
                                            "option",
                                            selectedScrutinyDetails?.option_c,
                                            `${base64ToString(
                                              `${
                                                getCorrectOption(
                                                  selectedScrutinyDetails
                                                )?.selected_option_image
                                              }`
                                            )}`,
                                            "option_c_img"
                                          )
                                        }
                                      />
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-full grid grid-cols-12 gap-4 mt-4 border border-gray-100 shadow-sm py-4 px-2 rounded-md">
                      <div className={!!selectedScrutinyDetails &&
                        selectedScrutinyDetails?.accept_flag !== null ?"col-span-6":"col-span-12"}>
                        <CustomAccordion
                          title={"Candidate Remarks"}
                          isExpanded={candidateRemarks}
                          setExpanded={setCandidateRemarks}
                          ternary
                          // highlightColor={"bg-[#c026d3]"}
                        >
                          <div className="p-3 my-2  grid grid-cols-12 gap-4">
                            <div className="col-span-12">
                              <p className=" ">Candidate Remarks</p>
                              <div className="mt-2 font-semibold border p-3 rounded-sm">
                                {selectedScrutinyDetails?.remarks ?? "N/A"}
                              </div>
                            </div>
                            {selectedScrutinyDetails?.supporting_docs ? (
                              <div className="col-span-12 mt-5">
                                <p className=" ">
                                  Candidate Supporting Document
                                </p>
                                <div className="mt-2  ">
                                  <button
                                    onClick={() =>
                                      downloadBase64PDF(
                                        base64ToString(
                                          selectedScrutinyDetails?.supporting_docs
                                        ),
                                        "candidate-supporting-document.pdf"
                                      )
                                    }
                                    className="px-3 py-1 flex items-center gap-x-2 bg-blue-500 rounded-sm"
                                  >
                                    <Download
                                      className="text-white"
                                      size={15}
                                    />
                                    <p className="text-sm text-white">
                                      Download
                                    </p>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="col-span-12 mt-5">
                                  <p className=" ">
                                    Candidate Supporting Document
                                  </p>
                                  <div className="mt-2  "></div>
                                  <div>
                                    <p className="text-blue-600">
                                      No Document Submitted
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </CustomAccordion>
                      </div>

                      <div className="col-span-6">
                        {!!selectedScrutinyDetails &&
                        selectedScrutinyDetails?.accept_flag !== null ? (
                          <CustomAccordion
                            title={
                              selectedScrutinyDetails?.accept_flag === true
                                ? `Approval Document`
                                : `Rejection Document`
                            }
                            isExpanded={candidateRemarks}
                            setExpanded={setCandidateRemarks}
                            primary
                            // highlightColor={"bg-[#c026d3]"}
                          >
                            <div className="p-3 my-2  grid grid-cols-12 gap-4">
                              <div className="col-span-12">
                                {!!selectedScrutinyDetails &&
                                selectedScrutinyDetails?.accept_comment &&
                                selectedScrutinyDetails?.accept_flag !==
                                  null ? (
                                  <div className="col-span-12">
                                    <p className=" ">
                                      {selectedScrutinyDetails?.accept_flag ===
                                      true
                                        ? `Approval Remarks From Authority`
                                        : `Rejection Remarks From Authority `}
                                    </p>
                                    <div className="mt-2 font-semibold border p-3 rounded-sm">
                                      {selectedScrutinyDetails?.accept_comment}
                                    </div>
                                  </div>
                                ) : (
                                  <></>
                                )}
                              </div>
                              {selectedScrutinyDetails?.approval_docs ? (
                                <div className="col-span-12 mt-5">
                                  <p className=" ">
                                    {selectedScrutinyDetails?.accept_flag ===
                                    true
                                      ? `Approval Document From Authority`
                                      : `Rejection Document From Authority `}
                                  </p>
                                  <div className="mt-2  ">
                                    <button
                                      onClick={() =>
                                        downloadBase64PDF(
                                          base64ToString(
                                            selectedScrutinyDetails?.approval_docs
                                          ),
                                          selectedScrutinyDetails?.accept_flag ===
                                            true
                                            ? `approval-document.pdf`
                                            : "rejection-document.pdf"
                                        )
                                      }
                                      className="px-3 py-1 flex items-center gap-x-2 bg-blue-500 rounded-sm"
                                    >
                                      <Download
                                        className="text-white"
                                        size={15}
                                      />
                                      <p className="text-sm text-white">
                                        Download
                                      </p>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="col-span-12 mt-5">
                                    <p className=" ">
                                      {selectedScrutinyDetails?.accept_flag ===
                                      true
                                        ? `Approval Document From Authority`
                                        : `Rejection Document From Authority `}
                                    </p>
                                    <div className="mt-2  "></div>
                                    <div>
                                      <p className="text-blue-600">
                                        No Document Submitted
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </CustomAccordion>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  </div>
                  <ZoomModal
                    isOpen={zoomModal.isOpen}
                    onClose={closeZoom}
                    content={zoomModal.content}
                  />
                </CustomAccordion>
              ) : (
                <></>
              )}
            </div>
          </DialogContent>
        </>
      ) : (
        <></>
      )}
    </Dialog>
  );
};

export default ScrutinyDetails;
